// Roots mark and wordmark — brand_assets.md §4 rows 9-10. Colour comes from `currentColor`,
// so callers set it with a text colour token.
import type { ReactNode } from 'react';
import { MARK, MARK_SIMPLE, WORDMARK } from './paths.js';

interface BrandProps {
  className?: string;
  /** Accessible name; pass `null` when the surrounding text already says "Roots". */
  title?: string | null;
}

function BrandSvg({
  viewBox,
  className,
  title,
  children,
}: BrandProps & { viewBox: string; children: ReactNode }) {
  if (title === null) {
    return (
      <svg viewBox={viewBox} className={className} fill="currentColor" aria-hidden="true">
        {children}
      </svg>
    );
  }
  return (
    <svg viewBox={viewBox} className={className} fill="currentColor" role="img">
      <title>{title ?? 'Roots'}</title>
      {children}
    </svg>
  );
}

/** The "r" with roots. `simple` is the small-size cut (three roots) for anything under ~40px. */
export function LogoMark({ simple = false, ...props }: BrandProps & { simple?: boolean }) {
  const mark = simple ? MARK_SIMPLE : MARK;
  return (
    <BrandSvg viewBox={mark.viewBox} {...props}>
      <g transform={mark.transform}>
        {mark.paths.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </BrandSvg>
  );
}

/** Horizontal lockup: mark + "roots". Size it by height; width follows. */
export function Wordmark(props: BrandProps) {
  return (
    <BrandSvg viewBox={WORDMARK.viewBox} {...props}>
      <g transform={WORDMARK.mark.transform}>
        {WORDMARK.mark.paths.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
      {WORDMARK.glyphs.map((glyph) => (
        <path key={glyph.transform} transform={glyph.transform} d={glyph.d} />
      ))}
    </BrandSvg>
  );
}
